import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, DollarSign, TrendingUp, TrendingDown, Users,
  PieChart, BarChart3, Activity, Calendar
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SuperAdminRevenue = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchRevenue();
  }, []);

  const fetchRevenue = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(`${BACKEND_URL}/api/super-admin/revenue`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (error) {
      console.error('Failed to fetch revenue:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load revenue data');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getPlanColor = (plan) => {
    const colors = {
      free: 'bg-gray-500',
      starter: 'bg-blue-500',
      growth: 'bg-emerald-500',
      agency: 'bg-amber-500'
    };
    return colors[plan] || colors.free;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1e] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
      </div>
    );
  }

  const planData = data?.plan_breakdown || {};
  const totalUsers = Object.values(planData).reduce((sum, p) => sum + (p.count || 0), 0);

  return (
    <div className="min-h-screen bg-[#0a0f1e] p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <button
            onClick={() => navigate('/super-admin')}
            className="flex items-center gap-2 text-[#94a3b8] hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Admin
          </button>
          <h1 className="text-3xl font-bold text-white">Revenue Dashboard</h1>
          <p className="text-[#94a3b8]">Financial overview and growth metrics</p>
        </div>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="bg-gradient-to-br from-emerald-900/50 to-emerald-800/30 border-emerald-500/30">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className="w-5 h-5 text-emerald-400" />
                <span className="text-emerald-400 text-sm">MRR</span>
              </div>
              <p className="text-3xl font-bold text-white">{formatCurrency(data?.mrr || 0)}</p>
              <p className="text-xs text-emerald-300/70 mt-1">Monthly Recurring Revenue</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
          <Card className="bg-gradient-to-br from-blue-900/50 to-blue-800/30 border-blue-500/30">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-2">
                <BarChart3 className="w-5 h-5 text-blue-400" />
                <span className="text-blue-400 text-sm">ARR</span>
              </div>
              <p className="text-3xl font-bold text-white">{formatCurrency(data?.arr || 0)}</p>
              <p className="text-xs text-blue-300/70 mt-1">Annual Run Rate</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="bg-[#1a2235] border-[#1e3a5f30]">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-5 h-5 text-green-400" />
                <span className="text-[#94a3b8] text-sm">New MRR</span>
              </div>
              <p className="text-3xl font-bold text-white">{formatCurrency(data?.new_mrr_this_month || 0)}</p>
              <p className="text-xs text-[#94a3b8] mt-1">This month</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <Card className="bg-[#1a2235] border-[#1e3a5f30]">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-2">
                <TrendingDown className="w-5 h-5 text-red-400" />
                <span className="text-[#94a3b8] text-sm">Churned MRR</span>
              </div>
              <p className="text-3xl font-bold text-white">{formatCurrency(data?.churned_mrr || 0)}</p>
              <p className="text-xs text-[#94a3b8] mt-1">This month</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="bg-[#1a2235] border-[#1e3a5f30]">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-2">
                <Activity className="w-5 h-5 text-violet-400" />
                <span className="text-[#94a3b8] text-sm">Growth</span>
              </div>
              <p className={`text-3xl font-bold ${data?.growth_rate >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {data?.growth_rate >= 0 ? '+' : ''}{data?.growth_rate || 0}%
              </p>
              <p className="text-xs text-[#94a3b8] mt-1">Net Revenue Growth</p>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Plan Breakdown */}
        <Card className="bg-[#1a2235] border-[#1e3a5f30]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <PieChart className="w-5 h-5 text-emerald-400" />
              Revenue by Plan
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {['free', 'starter', 'growth', 'agency'].map((plan) => {
                const planInfo = planData[plan] || { count: 0, revenue: 0 };
                const pricing = { free: 0, starter: 999, growth: 2999, agency: 9999 };
                const percent = totalUsers > 0 ? Math.round((planInfo.count / totalUsers) * 100) : 0;

                return (
                  <div key={plan} className="flex items-center gap-4">
                    <div className={`w-3 h-3 rounded-full ${getPlanColor(plan)}`}></div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-white capitalize font-medium">{plan}</span>
                        <span className="text-[#94a3b8]">{planInfo.count} users</span>
                      </div>
                      <div className="w-full h-2 bg-[#0a0f1e] rounded-full overflow-hidden">
                        <div
                          className={`h-full ${getPlanColor(plan)}`}
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                    </div>
                    <div className="text-right min-w-[80px]">
                      <p className="text-white font-medium">{formatCurrency(planInfo.revenue)}</p>
                      <p className="text-xs text-[#94a3b8]">₹{pricing[plan]}/mo</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 pt-6 border-t border-[#1e3a5f30]">
              <div className="flex items-center justify-between">
                <span className="text-white font-semibold">Total MRR</span>
                <span className="text-2xl font-bold text-emerald-400">{formatCurrency(data?.mrr || 0)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Client Growth */}
        <Card className="bg-[#1a2235] border-[#1e3a5f30]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-400" />
              Client Growth
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-6 mb-6">
              <div className="p-4 bg-[#0a0f1e] rounded-xl">
                <p className="text-3xl font-bold text-white">{data?.new_clients_this_month || 0}</p>
                <p className="text-[#94a3b8] text-sm">New this month</p>
              </div>
              <div className="p-4 bg-[#0a0f1e] rounded-xl">
                <p className="text-3xl font-bold text-white">{data?.new_clients_last_month || 0}</p>
                <p className="text-[#94a3b8] text-sm">Last month</p>
              </div>
            </div>

            {/* MRR History Chart (simplified bar) */}
            <div>
              <h4 className="text-white font-medium mb-4">MRR Trend</h4>
              <div className="flex items-end gap-2 h-32">
                {data?.mrr_history?.map((item, index) => {
                  const maxMrr = Math.max(...(data.mrr_history?.map(h => h.mrr) || [1]));
                  const height = (item.mrr / maxMrr) * 100;
                  
                  return (
                    <div key={index} className="flex-1 flex flex-col items-center gap-2">
                      <div
                        className="w-full bg-gradient-to-t from-emerald-600 to-emerald-400 rounded-t-sm transition-all"
                        style={{ height: `${height}%` }}
                      />
                      <span className="text-xs text-[#94a3b8] truncate">{item.month?.split(' ')[0]}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-[#1a2235] border-[#1e3a5f30]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#94a3b8] text-sm">Average Revenue Per User</p>
                <p className="text-2xl font-bold text-white">
                  {formatCurrency(totalUsers > 0 ? Math.round((data?.mrr || 0) / totalUsers) : 0)}
                </p>
              </div>
              <div className="p-3 bg-amber-500/20 rounded-xl">
                <DollarSign className="w-6 h-6 text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#1a2235] border-[#1e3a5f30]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#94a3b8] text-sm">Paid Conversion Rate</p>
                <p className="text-2xl font-bold text-white">
                  {totalUsers > 0 
                    ? Math.round(((totalUsers - (planData.free?.count || 0)) / totalUsers) * 100) 
                    : 0}%
                </p>
              </div>
              <div className="p-3 bg-violet-500/20 rounded-xl">
                <TrendingUp className="w-6 h-6 text-violet-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#1a2235] border-[#1e3a5f30]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#94a3b8] text-sm">Total Paying Customers</p>
                <p className="text-2xl font-bold text-white">
                  {(planData.starter?.count || 0) + (planData.growth?.count || 0) + (planData.agency?.count || 0)}
                </p>
              </div>
              <div className="p-3 bg-emerald-500/20 rounded-xl">
                <Users className="w-6 h-6 text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SuperAdminRevenue;
